"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_LOCAL_STORE_DATABASE = void 0;
exports.sealAppLocalData = sealAppLocalData;
exports.createAppLocalStore = createAppLocalStore;
const local_data_seal_1 = require("./local-data-seal");
exports.APP_LOCAL_STORE_DATABASE = 'streamlain-app-data';
const WINDOWS = 'windows';
const PENDING = 'pending';
const KEY = 'key';
const WRITE_KEY = 'write';
function openAppLocalDatabase(factory, onClosed) {
    return new Promise((resolve, reject) => {
        const request = factory.open(exports.APP_LOCAL_STORE_DATABASE, 1);
        request.onupgradeneeded = () => {
            request.result.createObjectStore(WINDOWS);
            request.result.createObjectStore(PENDING, { keyPath: 'id' });
            request.result.createObjectStore(KEY);
        };
        request.onsuccess = () => {
            // Another tab deletes or seals the database: give it up.
            request.result.onversionchange = () => {
                request.result.close();
                onClosed();
            };
            resolve(request.result);
        };
        request.onerror = () => {
            onClosed();
            reject(request.error);
        };
    });
}
async function runIn(database, store, mode, action) {
    const transaction = database.transaction(store, mode);
    const request = action(transaction.objectStore(store));
    return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve(request ? request.result : undefined);
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
    });
}
/**
 * The session of the address ended (clear-origin): the windows go, the key
 * of the kept writes is forgotten; the sealed writes and the envelope of
 * their key stay for the same person.
 */
async function sealAppLocalData(factory) {
    const database = await openAppLocalDatabase(factory, () => { });
    try {
        await runIn(database, WINDOWS, 'readwrite', store => {
            store.clear();
        });
        const kept = (await runIn(database, KEY, 'readonly', store => store.get(WRITE_KEY)));
        if (kept?.kek)
            await runIn(database, KEY, 'readwrite', store => {
                store.put({ ...kept, kek: null }, WRITE_KEY);
            });
    }
    finally {
        database.close();
    }
}
function keyMaterialBytes(keyMaterial) {
    try {
        return Uint8Array.from(atob(keyMaterial), char => char.charCodeAt(0));
    }
    catch {
        throw (0, local_data_seal_1.localAccountKeyError)('KEY_UNAVAILABLE');
    }
}
/**
 * The store of the person an App release confirmation names; null for a
 * public visitor, who keeps no data.
 */
function createAppLocalStore(input) {
    const { payload } = input.envelope;
    const { principal } = payload;
    if (principal.type === 'public')
        return null;
    const grant = payload.grants[0];
    // The person and App a kept write belongs to; the record id is its revision.
    const scopeOf = (id) => ({
        accountId: principal.type === 'workspace-member'
            ? `workspace-member:${principal.userId}`
            : `portal-member:${principal.portalIdentityId}`,
        workspaceId: payload.workspaceId,
        docId: grant.appBaseId,
        kind: 'app-write',
        revision: id,
    });
    const now = input.now ?? (() => new Date());
    const expiresAt = Date.parse(payload.expiresAt);
    const granted = (sourceKey) => grant.sourceKeys.includes(sourceKey) && now().getTime() < expiresAt;
    let opened = null;
    const database = () => (opened ??= openAppLocalDatabase(input.factory, () => {
        opened = null;
    }));
    const run = async (store, mode, action) => runIn(await database(), store, mode, action);
    const keptKey = async () => (await run(KEY, 'readonly', store => store.get(WRITE_KEY)));
    const sealer = new local_data_seal_1.LocalDataSealer({
        async getKey() {
            const kept = await keptKey();
            if (!kept?.kek)
                throw (0, local_data_seal_1.localAccountKeyError)('LOCKED');
            return {
                keyId: kept.keyId,
                key: await (0, local_data_seal_1.unwrapLocalDocKey)(kept.kek, kept.wrapped),
            };
        },
    });
    const encoder = new TextEncoder();
    const windowKey = (sourceKey, key) => JSON.stringify([sourceKey, key]);
    return {
        async readWindow(sourceKey, key) {
            if (!granted(sourceKey))
                return null;
            return ((await run(WINDOWS, 'readonly', store => store.get(windowKey(sourceKey, key)))) ?? null);
        },
        async keepWindow(sourceKey, key, window) {
            if (!granted(sourceKey))
                return;
            await run(WINDOWS, 'readwrite', store => {
                store.put(window, windowKey(sourceKey, key));
            });
        },
        async writeKey() {
            const kept = await keptKey();
            if (!kept)
                return { state: 'none' };
            return kept.kek
                ? { state: 'open' }
                : { state: 'sealed', envelope: kept.envelope };
        },
        async createWriteKey(wrap) {
            // A second key would leave the sealed writes of the first unreadable.
            if (await keptKey())
                throw (0, local_data_seal_1.localAccountKeyError)('KEY_UNAVAILABLE');
            const material = crypto.getRandomValues(new Uint8Array(32));
            try {
                const { envelope, keyId } = await wrap(btoa(String.fromCharCode(...material)));
                const kek = await (0, local_data_seal_1.importLocalDataKek)(material);
                const wrapped = await (0, local_data_seal_1.wrapLocalDocKey)(kek);
                await run(KEY, 'readwrite', store => {
                    store.put({ envelope, keyId, wrapped, kek }, WRITE_KEY);
                });
            }
            finally {
                material.fill(0);
            }
        },
        async openWriteKey(keyMaterial) {
            const kept = await keptKey();
            if (!kept)
                throw (0, local_data_seal_1.localAccountKeyError)('KEY_UNAVAILABLE');
            const material = keyMaterialBytes(keyMaterial);
            try {
                const kek = await (0, local_data_seal_1.importLocalDataKek)(material);
                // A key of another person or App does not unwrap: CORRUPT.
                await (0, local_data_seal_1.unwrapLocalDocKey)(kek, kept.wrapped);
                await run(KEY, 'readwrite', store => {
                    store.put({ ...kept, kek }, WRITE_KEY);
                });
            }
            finally {
                material.fill(0);
            }
        },
        async writePending(write) {
            const sources = Object.keys(write.command.pending);
            if (!sources.every(granted))
                return;
            const sealed = await sealer.seal(scopeOf(write.id), encoder.encode(JSON.stringify(write)));
            await run(PENDING, 'readwrite', store => {
                store.put({ id: write.id, sources, sealed });
            });
        },
        async pendingWrites() {
            if (now().getTime() >= expiresAt)
                return [];
            if (!(await keptKey())?.kek)
                return [];
            const kept = (await run(PENDING, 'readonly', store => store.getAll())) ?? [];
            return Promise.all(kept.map(async ({ id, sealed }) => JSON.parse(new TextDecoder().decode(await sealer.open(scopeOf(id), sealed)))));
        },
        async dropPending(id) {
            await run(PENDING, 'readwrite', store => {
                store.delete(id);
            });
        },
        async pruneToGrant(next) {
            const kept = new Set(next.sourceKeys);
            const [windowKeys, pending] = await Promise.all([
                run(WINDOWS, 'readonly', store => store.getAllKeys()),
                run(PENDING, 'readonly', store => store.getAll()),
            ]);
            await run(WINDOWS, 'readwrite', store => {
                for (const key of windowKeys ?? []) {
                    const [sourceKey] = JSON.parse(String(key));
                    if (!kept.has(sourceKey))
                        store.delete(key);
                }
            });
            await run(PENDING, 'readwrite', store => {
                for (const write of pending ?? [])
                    if (write.sources.some(key => !kept.has(key)))
                        store.delete(write.id);
            });
        },
        async clear() {
            await run(WINDOWS, 'readwrite', store => {
                store.clear();
            });
            await run(PENDING, 'readwrite', store => {
                store.clear();
            });
            await run(KEY, 'readwrite', store => {
                store.clear();
            });
        },
    };
}
